// AES-256-GCM encryption for local snapshots
// Uses Web Crypto API for browser-native encryption

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const ITERATIONS = 100000;

// Derive a key from password using PBKDF2
const deriveKey = async (password, salt) => {
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        'PBKDF2',
        false,
        ['deriveKey']
    );
    
    return window.crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt,
            iterations: ITERATIONS,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: ALGORITHM, length: KEY_LENGTH },
        false,
        ['encrypt', 'decrypt']
    );
};

// Encrypt data with password
export const encryptData = async (data, password) => {
    try {
        const encoder = new TextEncoder();
        const plaintext = encoder.encode(JSON.stringify(data));
        
        // Generate random salt and IV
        const salt = window.crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
        const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
        
        // Derive key from password
        const key = await deriveKey(password, salt);
        
        // Encrypt
        const ciphertext = await window.crypto.subtle.encrypt(
            { name: ALGORITHM, iv },
            key,
            plaintext
        );
        
        // Combine salt + iv + ciphertext
        const combined = new Uint8Array(
            salt.length + iv.length + ciphertext.byteLength
        );
        combined.set(salt, 0);
        combined.set(iv, salt.length);
        combined.set(new Uint8Array(ciphertext), salt.length + iv.length);
        
        // Return as base64
        return btoa(String.fromCharCode(...combined));
    } catch (error) {
        throw new Error('Encryption failed: ' + error.message);
    }
};

// Decrypt data with password
export const decryptData = async (encryptedBase64, password) => {
    try {
        // Decode from base64
        const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
        
        // Extract salt, iv, and ciphertext
        const salt = combined.slice(0, SALT_LENGTH);
        const iv = combined.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
        const ciphertext = combined.slice(SALT_LENGTH + IV_LENGTH);
        
        // Derive key from password
        const key = await deriveKey(password, salt);
        
        // Decrypt
        const plaintext = await window.crypto.subtle.decrypt(
            { name: ALGORITHM, iv },
            key,
            ciphertext
        );
        
        // Parse JSON
        const decoder = new TextDecoder();
        return JSON.parse(decoder.decode(plaintext));
    } catch (error) {
        throw new Error('Decryption failed. Please check your password.');
    }
};

// Generate a random encryption key (for display to user)
export const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => chars[byte % chars.length]).join('');
};
